import { useCallback } from 'react';
import { PlaylistService } from '@/services/playlist.service';
import { useFirestoreSubscription } from './useFirestoreSubscription';
import type { Playlist, UpdatePlaylistData } from '@/types';

interface UsePlaylistResult {
    playlist: Playlist | null;
    isLoading: boolean;
    error: Error | null;
    updatePlaylist: (updates: UpdatePlaylistData) => Promise<void>;
    deletePlaylist: () => Promise<void>;
}

export function usePlaylist(playlistId: string | undefined): UsePlaylistResult {
    const { data, isLoading, error } = useFirestoreSubscription<Playlist | null>(
        (callback) => {
            if (!playlistId) {
                callback(null);
                return () => { };
            }
            return PlaylistService.subscribeToPlaylist(playlistId, callback);
        },
        [playlistId]
    );

    const updatePlaylist = useCallback(async (updates: UpdatePlaylistData) => {
        if (!playlistId) return;
        await PlaylistService.updatePlaylist(playlistId, updates);
    }, [playlistId]);

    const deletePlaylist = useCallback(async () => {
        if (!playlistId) return;
        await PlaylistService.deletePlaylist(playlistId);
    }, [playlistId]);

    return {
        playlist: data,
        isLoading,
        error,
        updatePlaylist,
        deletePlaylist,
    };
}
